import { DynamicFurnitureSVG } from "@gettactic/map-builder/src/components/map/shapes/DynamicFurnitureShape/DynamicFurnitureSVG";
import { SVGShapeRenderer } from "@gettactic/map-builder/src/components/ViewSVG/SVGShapeRenderer";
import type { Shape } from "@gettactic/map-builder/src/types";
import React from "react";
import { useRenderOptions } from "./RenderOptionsContext";
import { CircleSvg } from "./shapes/CircleSvg";
import { FurnitureSvg } from "./shapes/FurnitureSvg";
import { GroupSvg } from "./shapes/GroupSvg";
import { IconSvg } from "./shapes/IconSvg";
import { ImageSvg } from "./shapes/ImageSvg";
import { RectangleSvg } from "./shapes/RectangleSvg";
import { SpotSvg } from "./shapes/SpotSvg";
import { TextSvg } from "./shapes/TextSvg";
import { WallPathSvg } from "./shapes/WallPathSvg";
import { WallSvg } from "./shapes/WallSvg";
import { ZoneSvg } from "./shapes/ZoneSvg";

export interface ShapeSvgProps {
  shape: Shape;
  parentGroupColor?: string;
}

/** Draws one shape by type. Pure with respect to the shape object, so the
 *  memoised `ShapeNode` above it decides when this runs. */
export const ShapeSvg: React.FC<ShapeSvgProps> = ({
  shape,
  parentGroupColor
}) => {
  const {
    zoneTypes,
    teamColors,
    interactiveZones,
    isViewMode,
    highlightResource,
    zoneView
  } = useRenderOptions();
  switch (shape.type) {
    case "rectangle":
      return <RectangleSvg shape={shape} />;
    case "circle":
      return <CircleSvg shape={shape} />;
    case "text":
      return <TextSvg shape={shape} />;
    case "image":
      return <ImageSvg shape={shape} />;
    case "svg":
      return <SVGShapeRenderer shape={shape} />;
    case "icon":
      return <IconSvg shape={shape} />;
    case "dynamic-furniture":
      return <DynamicFurnitureSVG shape={shape} />;
    case "furniture":
      return <FurnitureSvg shape={shape} />;
    case "spot":
      return (
        <SpotSvg
          shape={shape}
          parentGroupColor={parentGroupColor}
          teamColors={teamColors}
          interactiveZone={
            shape.resource_id
              ? interactiveZones?.[shape.resource_id]
              : undefined
          }
          isViewMode={isViewMode}
          highlightResource={highlightResource}
        />
      );
    case "zone":
      return (
        <ZoneSvg shape={shape} zoneTypes={zoneTypes} zoneView={zoneView} />
      );
    case "wall":
      return <WallSvg shape={shape} />;
    case "wall-path":
      return <WallPathSvg shape={shape} />;
    case "group":
      return <GroupSvg shape={shape} />;
    default:
      return null;
  }
};
